import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ROLE_LABELS } from "@/lib/constants";
import { ArrowLeft, Building2, UserCog, Users2, Car, ArrowRight } from "lucide-react";

export default function TenantDetail() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { selectTenant } = useTenant();
  const [tenant, setTenant] = useState(null);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [{ data: t }, { data: bs }, { data: us }, { data: st }] = await Promise.all([
          api.get(`/tenants/${tenantId}`),
          api.get(`/branches`, { params: { tenant_id: tenantId } }),
          api.get(`/users`, { params: { tenant_id: tenantId } }),
          api.get(`/dashboard/stats`, { params: { tenant_id: tenantId } }),
        ]);
        setTenant(t);
        setBranches(bs);
        setUsers(us);
        setStats(st);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [tenantId]);

  function enterAsTenant() {
    selectTenant(tenantId);
    navigate("/dashboard");
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24" />
        <Skeleton className="h-40" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!tenant) return null;

  return (
    <div className="space-y-8" data-testid="page-tenant-detail">
      <Button variant="ghost" size="sm" onClick={() => navigate("/tenants")} data-testid="tenant-detail-back">
        <ArrowLeft className="h-4 w-4 mr-2" /> Volver a tenants
      </Button>

      <PageHeader
        title={tenant.commercial_name}
        description={tenant.legal_name || "Detalle del tenant"}
        actions={
          <Button className="btn-tenant" onClick={enterAsTenant} data-testid="btn-operate-tenant">
            Operar este tenant <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatMini label="Sucursales" value={stats?.branches_count ?? 0} icon={Building2} />
        <StatMini label="Usuarios" value={stats?.users_count ?? 0} icon={UserCog} />
        <StatMini label="Clientes" value={stats?.customers_count ?? 0} icon={Users2} />
        <StatMini label="Vehículos" value={stats?.vehicles_count ?? 0} icon={Car} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="tracking-tight">Información</CardTitle>
            <CardDescription>Datos comerciales y fiscales.</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <dl className="space-y-3 text-sm">
              <Row label="RFC" value={<span className="mono">{tenant.rfc || "—"}</span>} />
              <Row label="Email" value={tenant.email || "—"} />
              <Row label="Teléfono" value={tenant.phone || "—"} />
              <Row label="Dirección" value={tenant.address || "—"} />
              <Row label="Plan" value={<Badge variant="secondary" className="capitalize">{tenant.plan_name}</Badge>} />
              <Row label="Estatus" value={<Badge className="capitalize">{tenant.account_status}</Badge>} />
              <Row
                label="Color primario"
                value={
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded border border-border" style={{ background: tenant.primary_color }} />
                    <span className="mono">{tenant.primary_color}</span>
                  </div>
                }
              />
            </dl>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="tracking-tight">Sucursales</CardTitle>
            <CardDescription>{branches.length} registrada{branches.length === 1 ? "" : "s"}</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {branches.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground">Sin sucursales.</div>
            ) : (
              <ul className="divide-y divide-border">
                {branches.map((b) => (
                  <li key={b.id} className="p-4 flex items-center justify-between">
                    <div>
                      <div className="font-medium flex items-center gap-2">
                        {b.name}
                        {b.is_main && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Principal</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {b.branch_type} · {b.code || ""}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">{b.address || ""}</div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="border-border lg:col-span-2">
          <CardHeader className="border-b border-border">
            <CardTitle className="tracking-tight">Usuarios del tenant</CardTitle>
            <CardDescription>{users.length} usuario{users.length === 1 ? "" : "s"}</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {users.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground">Sin usuarios.</div>
            ) : (
              <ul className="divide-y divide-border">
                {users.map((u) => (
                  <li key={u.id} className="p-4 flex items-center justify-between">
                    <div>
                      <div className="font-medium">{u.full_name}</div>
                      <div className="text-xs text-muted-foreground">{u.email}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="border-tenant text-tenant">
                        {ROLE_LABELS[u.role_name] || u.role_name}
                      </Badge>
                      <Badge className={u.status === "active" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : "bg-slate-100 text-slate-700 hover:bg-slate-100"}>
                        {u.status === "active" ? "Activo" : "Inactivo"}
                      </Badge>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start gap-4">
      <dt className="text-xs uppercase tracking-wider text-muted-foreground w-28 shrink-0 pt-0.5">{label}</dt>
      <dd className="text-sm">{value}</dd>
    </div>
  );
}

function StatMini({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-border rounded-lg p-4 flex items-center gap-3">
      <div className="h-9 w-9 rounded-md bg-slate-100 flex items-center justify-center text-slate-600">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <div className="text-[11px] uppercase text-muted-foreground tracking-wider">{label}</div>
        <div className="text-xl font-semibold" style={{ fontFamily: "Chivo, sans-serif" }}>{value}</div>
      </div>
    </div>
  );
}
